import React, { useCallback, useMemo, useState } from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Dialog from "../shared/Dialog";
import useModal from "../../hooks/useModal";

const AssociateNewsModal = ({
  cidOptions = [],
  initialCids = [],
  initialJourneys = [],
  journeyOptions = [],
  newsOptions = [],
  onClose,
  onConfirm,
} = {}) => {
  const [selectedNewsIds, setSelectedNewsIds] = useState([]);
  const [selectedCids, setSelectedCids] = useState(initialCids);
  const [selectedJourneys, setSelectedJourneys] = useState(initialJourneys);

  const cidById = useMemo(
    () => new Map((cidOptions || []).map((o) => [o.id, o])),
    [cidOptions]
  );
  const journeyById = useMemo(
    () => new Map((journeyOptions || []).map((o) => [o.id, o])),
    [journeyOptions]
  );
  const newsById = useMemo(
    () => new Map((newsOptions || []).map((o) => [o.id, o])),
    [newsOptions]
  );

  const { isLoading, handleConfirm } = useModal({ onClose, onConfirm });

  const handleAssociate = useCallback(() => {
    handleConfirm({
      cids: selectedCids,
      journeys: selectedJourneys,
      newsIds: selectedNewsIds,
    });
  }, [handleConfirm, selectedCids, selectedJourneys, selectedNewsIds]);

  return (
    <Dialog fullWidth open maxWidth="sm">
      <DialogTitle>Associate news to CID and journey</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Autocomplete
            disableCloseOnSelect
            multiple
            getOptionLabel={(option) => option.label}
            options={newsOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="News"
                placeholder="Search news..."
                size="small"
                variant="outlined"
              />
            )}
            value={(selectedNewsIds || [])
              .map((id) => newsById.get(id))
              .filter(Boolean)}
            onChange={(e, value) =>
              setSelectedNewsIds((value || []).map((v) => v.id))
            }
          />
        </Box>

        <Box mt={2}>
          <Autocomplete
            disableCloseOnSelect
            multiple
            getOptionLabel={(option) => option.label}
            options={cidOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="CID"
                placeholder="Search CID..."
                size="small"
                variant="outlined"
              />
            )}
            value={(selectedCids || [])
              .map((id) => cidById.get(id))
              .filter(Boolean)}
            onChange={(e, value) =>
              setSelectedCids((value || []).map((v) => v.id))
            }
          />
        </Box>

        <Box mt={2}>
          <Autocomplete
            disableCloseOnSelect
            multiple
            getOptionLabel={(option) => option.label}
            options={journeyOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="Journey"
                placeholder="Search journey..."
                size="small"
                variant="outlined"
              />
            )}
            value={(selectedJourneys || [])
              .map((id) => journeyById.get(id))
              .filter(Boolean)}
            onChange={(e, value) =>
              setSelectedJourneys((value || []).map((v) => v.id))
            }
          />
        </Box>

        <Box mt={2}>
          <Typography color="textSecondary" component="p" variant="caption">
            Selections are merged into each news item&apos;s existing CID and
            journey lists (duplicates removed).
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={handleAssociate}>
          {isLoading ? (
            <Box alignItems="center" display="flex">
              <CircularProgress color="inherit" size={18} />
              <Box component="span" ml={1}>
                Applying...
              </Box>
            </Box>
          ) : (
            "Apply"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssociateNewsModal;
